import { useCallback, useEffect, useMemo, useRef } from "react";
import { useOrganization, useUser } from "@clerk/react";

// The most members read for names. A bigger org's people past it show by a
// short id (`capped` says so); reading on would make one page load ten
// requests and more.
const MAX_MEMBERS = 1000;

// Display names for the user ids Convex returns: Convex holds no names, so they
// come from Clerk's member list, which admins can read. The viewer is "You"; an
// id Clerk does not list (a removed member) shows as a short id. Clerk returns
// the list 100 at a time, so every page is read, up to MAX_MEMBERS, for the
// sessions page's person filter to list everyone.
export function usePeople(): {
  nameFor: (userId: string) => string;
  people: Array<{ userId: string; name: string }>;
  /** True when the list stopped at MAX_MEMBERS with more members unread. */
  capped: boolean;
} {
  const { user } = useUser();
  const { organization, memberships } = useOrganization({
    memberships: { pageSize: 100, infinite: true, keepPreviousData: true },
  });
  const me = user?.id ?? null;
  const loaded = memberships?.data?.length ?? 0;
  const hasNextPage = memberships?.hasNextPage === true;
  const isFetching = memberships?.isFetching === true;
  const fetchNext = memberships?.fetchNext;
  const orgId = organization?.id ?? null;
  // The org and the count of its members loaded when the last page was asked
  // for, so a render before Clerk starts fetching does not ask for the same
  // page twice, and a newly active org is paged from its own first page.
  const askedAt = useRef<{ orgId: string | null; loaded: number } | null>(null);
  useEffect(() => {
    if (!hasNextPage || isFetching || loaded >= MAX_MEMBERS) return;
    const last = askedAt.current;
    if (last && last.orgId === orgId && last.loaded === loaded) return;
    askedAt.current = { orgId, loaded };
    fetchNext?.();
  }, [hasNextPage, isFetching, loaded, fetchNext, orgId]);
  const people = useMemo(() => {
    const out: Array<{ userId: string; name: string }> = [];
    for (const m of memberships?.data ?? []) {
      const pud = m.publicUserData;
      if (!pud?.userId) continue;
      const full = [pud.firstName, pud.lastName].filter(Boolean).join(" ").trim();
      out.push({
        userId: pud.userId,
        name: pud.userId === me ? "You" : full || pud.identifier || pud.userId,
      });
    }
    return out;
  }, [memberships?.data, me]);
  const nameFor = useCallback(
    (userId: string) => {
      if (userId === me) return "You";
      const hit = people.find((p) => p.userId === userId);
      if (hit) return hit.name;
      return userId.length > 14 ? `${userId.slice(0, 10)}…` : userId;
    },
    [people, me]
  );
  return { nameFor, people, capped: loaded >= MAX_MEMBERS && hasNextPage };
}
